import { useState } from 'react';
import { Block, BlockTitle, Button, List, ListItem, Navbar, Page, f7 } from 'framework7-react';
import type { OrderTypeConfig, OrderTypeConfigBody } from '@restaurant/api-client';
import {
  usePushPermission,
  type PushPermissionStatus,
} from '@/features/notifications/hooks/usePushPermission';
import type { PushPlatform } from '@/features/notifications/hooks/usePushRegistration';
import { useSendTestPush } from '@/features/notifications/hooks/useSendTestPush';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useT } from '@/i18n';
import {
  useReservationConfig,
  useUpdateReservationConfig,
} from '@/features/reservation-setup/hooks/useReservationConfig';
import {
  useOrderTypeConfig,
  useUpdateOrderTypeConfig,
} from '@/features/order-setup/hooks/useOrderSetup';

type Notifications = ReturnType<typeof useT>['notifications'];

// Boolean-valued OrderTypeConfig fields that are also writable via the input.
type OrderBoolField = {
  [K in keyof OrderTypeConfig]: OrderTypeConfig[K] extends boolean ? K : never;
}[keyof OrderTypeConfig] &
  keyof OrderTypeConfigBody;

function deviceLabel(platform: PushPlatform, n: Notifications): string {
  switch (platform) {
    case 'ios':
      return n.deviceIphone;
    case 'android':
      return n.deviceAndroid;
    default:
      return n.deviceBrowser;
  }
}

// Each status maps to a short badge label, an F7 text-colour class for the
// badge, and a human explanation. `action` marks the one state (undecided
// permission) the user can resolve from inside the app.
function describeStatus(
  status: PushPermissionStatus,
  platform: PushPlatform,
  n: Notifications,
): { badge: string; colorClass: string; detail: string; action: boolean } {
  const native = platform !== 'web';
  switch (status) {
    case 'granted':
      return {
        badge: n.statusOn,
        colorClass: 'text-color-green',
        detail: n.statusOnDetail,
        action: false,
      };
    case 'prompt':
      return {
        badge: n.statusOff,
        colorClass: 'text-color-orange',
        detail: n.statusOffDetail,
        action: true,
      };
    case 'denied':
      return {
        badge: n.statusBlocked,
        colorClass: 'text-color-red',
        detail: native ? n.statusBlockedNative : n.statusBlockedWeb,
        action: false,
      };
    case 'browserBlocked':
      return {
        badge: n.statusBrowserBlocked,
        colorClass: 'text-color-red',
        detail: n.statusBrowserBlockedDetail,
        action: false,
      };
    case 'unsupported':
      return {
        badge: n.statusUnsupported,
        colorClass: 'text-color-gray',
        detail: n.statusUnsupportedDetail,
        action: false,
      };
    case 'unconfigured':
      return {
        badge: n.statusUnconfigured,
        colorClass: 'text-color-gray',
        detail: n.statusUnconfiguredDetail,
        action: false,
      };
    default:
      return {
        badge: n.statusChecking,
        colorClass: 'text-color-gray',
        detail: n.statusCheckingDetail,
        action: false,
      };
  }
}

// How long the backend waits before firing the test push. Short enough to keep
// the in-process timer honest, long enough to background the app first.
const TEST_DELAYS = [1, 5] as const;

function NotificationSettingsPage() {
  const t = useT();
  const n = t.notifications;
  const m = t.settings.mail;
  const settingsBackLink = useSettingsDetailBackLink();
  const { status, platform, enabling, enable } = usePushPermission();
  const [testDelay, setTestDelay] = useState<(typeof TEST_DELAYS)[number]>(5);
  const sendTestPush = useSendTestPush();

  const { data: resConfig } = useReservationConfig();
  const updateRes = useUpdateReservationConfig();
  const { data: orderConfigs } = useOrderTypeConfig();
  const updateOrder = useUpdateOrderTypeConfig();

  const s = describeStatus(status, platform, n);

  // Each flip commits immediately and confirms with a bottom toast - the switch
  // position IS the state, no Save button. Mirrors MailSettingsPage.
  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });
  const notify = {
    onSuccess: () => toast(t.saved),
    onError: (e: unknown) => toast(e instanceof Error ? e.message : t.error),
  };

  // Test push: the backend schedules it against this account's registered
  // devices and answers immediately, so the operator can background the app and
  // watch the real banner land. `scheduled: false` = no device registered yet.
  const sendTest = () =>
    sendTestPush.mutate(
      { delaySeconds: testDelay, title: n.testPushTitle, body: n.testPushBody },
      {
        onSuccess: (res) =>
          toast(res.scheduled ? n.testScheduled(res.delaySeconds) : n.testNoDevice),
        onError: notify.onError,
      },
    );

  // Plain controlled checkbox with F7's `.toggle` markup instead of the F7
  // <Toggle> component: in a mutation-driven, re-rendering settings list the
  // <Toggle>'s toggleChange event sometimes never wires, so taps did nothing.
  // Here React owns the checkbox. (Mirrors MailSettingsPage / ReservationSetupPage.)
  const toggleCtl = (opts: {
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <label className={`toggle${opts.disabled ? ' disabled' : ''}`} slot="after">
      <input
        type="checkbox"
        checked={opts.checked}
        disabled={opts.disabled}
        onChange={(e) => opts.onChange(e.target.checked)}
      />
      <span className="toggle-icon" />
    </label>
  );

  // --- Order owner-notify toggles (two axes: order type × payment) ---
  // A restaurant that never edited its order settings has no OrderTypeConfig row
  // yet - it is born on first write (all-true defaults). So toggles stay live
  // when a row is merely absent and reflect the schema defaults; only the
  // still-loading query greys them out.
  const pickup = orderConfigs?.find((c) => c.orderType === 'PICKUP');
  const delivery = orderConfigs?.find((c) => c.orderType === 'DELIVERY');
  const ordersLoading = orderConfigs === undefined;

  const setType = (type: 'PICKUP' | 'DELIVERY', data: OrderTypeConfigBody) =>
    updateOrder.mutate({ orderType: type, data }, notify);
  // Payment axis is shared across order types → write PICKUP + DELIVERY together
  // (toast once, on the second).
  const setBoth = (data: OrderTypeConfigBody) => {
    updateOrder.mutate({ orderType: 'PICKUP', data });
    updateOrder.mutate({ orderType: 'DELIVERY', data }, notify);
  };

  const typeRow = (type: 'PICKUP' | 'DELIVERY', field: OrderBoolField, label: string) => {
    const cfg = type === 'PICKUP' ? pickup : delivery;
    return (
      <ListItem title={label}>
        {toggleCtl({
          checked: cfg?.[field] ?? true,
          disabled: ordersLoading,
          onChange: (v) => setType(type, { [field]: v } as OrderTypeConfigBody),
        })}
      </ListItem>
    );
  };

  // One switch writing both order types. `defaultOn` mirrors the field's schema
  // default, so a restaurant with no config row yet reads the same as the server
  // would resolve it (payment gates default on, the prep reminder opt-in off).
  const payRow = (field: OrderBoolField, label: string, defaultOn = true, footer?: string) => (
    <ListItem title={label} footer={footer}>
      {toggleCtl({
        checked: (pickup?.[field] ?? defaultOn) && (delivery?.[field] ?? defaultOn),
        disabled: ordersLoading,
        onChange: (v) => setBoth({ [field]: v } as OrderTypeConfigBody),
      })}
    </ListItem>
  );

  const allPayRow = (
    cash: OrderBoolField,
    manual: OrderBoolField,
    online: OrderBoolField,
    label: string,
  ) => {
    const on = ([cash, manual, online] as OrderBoolField[]).every(
      (f) => (pickup?.[f] ?? true) && (delivery?.[f] ?? true),
    );
    return (
      <ListItem title={label}>
        {toggleCtl({
          checked: on,
          disabled: ordersLoading,
          onChange: (v) => setBoth({ [cash]: v, [manual]: v, [online]: v } as OrderTypeConfigBody),
        })}
      </ListItem>
    );
  };

  return (
    <Page name="notification-settings">
      <Navbar title={n.title} backLink={settingsBackLink} />

      <BlockTitle>{n.statusTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={deviceLabel(platform, n)} footer={s.detail}>
          <span slot="after" className={s.colorClass} style={{ fontWeight: 600 }}>
            {s.badge}
          </span>
        </ListItem>
      </List>
      {s.action && (
        <Block>
          <Button
            fill
            round
            preloader
            loading={enabling}
            disabled={enabling}
            onClick={() => void enable()}
          >
            {n.enableButton}
          </Button>
        </Block>
      )}

      {/* Prove the whole push path end-to-end from the device itself. */}
      <BlockTitle>{`${n.testSection} · ${n.testDelayTitle}`}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {TEST_DELAYS.map((d) => (
          <ListItem
            key={d}
            radio
            radioIcon="end"
            title={n.testDelaySeconds(d)}
            checked={testDelay === d}
            onChange={() => setTestDelay(d)}
          />
        ))}
      </List>
      <Block>
        <Button
          fill
          round
          preloader
          loading={sendTestPush.isPending}
          disabled={sendTestPush.isPending || status !== 'granted'}
          onClick={sendTest}
        >
          {n.testButton}
        </Button>
        <p className="text-color-gray" style={{ fontSize: '14px' }}>
          {n.testHint}
        </p>
      </Block>

      {/* Reservation owner/staff app alerts - backend-gated (reservation config). */}
      <BlockTitle>{n.reservationSection}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={n.newBooking}>
          {toggleCtl({
            checked: resConfig?.ownerNotifyEnabled ?? true,
            disabled: !resConfig,
            onChange: (v) => updateRes.mutate({ ownerNotifyEnabled: v }, notify),
          })}
        </ListItem>
        <ListItem title={n.cancellation}>
          {toggleCtl({
            checked: resConfig?.ownerNotifyCancellationEnabled ?? true,
            disabled: !resConfig,
            onChange: (v) => updateRes.mutate({ ownerNotifyCancellationEnabled: v }, notify),
          })}
        </ListItem>
        <ListItem title={n.noShow}>
          {toggleCtl({
            checked: resConfig?.ownerNotifyNoShowEnabled ?? true,
            disabled: !resConfig,
            onChange: (v) => updateRes.mutate({ ownerNotifyNoShowEnabled: v }, notify),
          })}
        </ListItem>
      </List>

      {/* Order owner/staff app alerts (push + inbox) - two axes: type × payment. */}
      <BlockTitle>{`${n.orderSection} · ${m.byOrderType}`}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {typeRow('PICKUP', 'ownerNotifyEnabled', m.pushPickupTitle)}
        {typeRow('DELIVERY', 'ownerNotifyEnabled', m.pushDeliveryTitle)}
      </List>
      <BlockTitle>{`${n.orderSection} · ${m.byPayment}`}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {allPayRow(
          'ownerNotifyCashEnabled',
          'ownerNotifyManualEnabled',
          'ownerNotifyOnlineEnabled',
          m.allMaster,
        )}
        {payRow('ownerNotifyCashEnabled', m.cash)}
        {payRow('ownerNotifyManualEnabled', m.manual)}
        {payRow('ownerNotifyOnlineEnabled', m.online)}
      </List>

      {/* Kitchen timing reminder - opt-in, off until the owner asks for it. */}
      <BlockTitle>{n.prepSection}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {payRow('prepReminderEnabled', n.prepReminder, false, n.prepReminderHint)}
      </List>

      <Block>
        <p className="text-color-gray" style={{ fontSize: '14px' }}>
          {n.hint}
        </p>
        <p className="text-color-gray" style={{ fontSize: '14px' }}>
          {m.ownerNote}
        </p>
      </Block>
    </Page>
  );
}

export default NotificationSettingsPage;
